import classNames from "classnames";
import * as React from "react";
import { getItemName, getItemValue } from "~/sharedComponents/list";
import { Marker } from "~/sharedComponents/userflow/utils/marker";
import { MultiSelectProps } from "./MultiSelect";
import * as styles from "./MultiSelectValuesView.less";
import { GetSelectedItemsProps, SelectedItems, SelectedItemsProps, useSelectedItems } from "./SelectedItems";

type MultiSelectValuesViewProps<TItem> = Marker &
    Omit<Partial<SelectedItemsProps<TItem>>, ""> &
    Partial<GetSelectedItemsProps<TItem>> &
    Pick<MultiSelectProps<TItem>, "isLoading"> & {
        /** Used to display some chips while all options list is still loading */
        initialOptions?: TItem[];
        disabled?: boolean;
        wrapperClassName?: string;
        tabIndex?: number;
    };

export const MultiSelectValuesView = <TItem,>({
    value,
    options,
    isLoading,
    initialOptions,
    onChange,
    disabled,
    wrapperClassName = styles.wrapper,
    showTitle = true,
    children, // AddLink
    "data-testid": dataTestId,
    getItemKey = getItemValue as any,
    getItemLabel = getItemName as any,
    tabIndex = 0,
    textForHighlight,
    ChipsComponent,
}: React.PropsWithChildren<MultiSelectValuesViewProps<TItem>>) => {
    const selectedItems = useSelectedItems({
        value,
        options: (isLoading && initialOptions) || options,
        getItemKey,
    });
    return (
        <div
            className={classNames(wrapperClassName, disabled && styles.disabled)}
            data-testid={dataTestId}
            tabIndex={tabIndex}
        >
            {!!value?.length && (
                <SelectedItems
                    textForHighlight={textForHighlight}
                    selectedItems={selectedItems}
                    onChange={!disabled && onChange}
                    showTitle={showTitle}
                    getItemKey={getItemKey}
                    getItemLabel={getItemLabel}
                    ChipsComponent={ChipsComponent}
                />
            )}
            {children}
        </div>
    );
};

type InlineMultiSelectViewer<TItem> = Omit<MultiSelectValuesViewProps<TItem>, "wrapperClassName" | "tabIndex"> & {
    isOpened: boolean;
};
export const InlineMultiSelectViewer = <TItem,>({ ...props }: InlineMultiSelectViewer<TItem>) => {
    return <MultiSelectValuesView {...props} wrapperClassName={styles.inline_edit} />;
};
